import { useMemo, useState } from 'react';
import type { ComponentType, FormEvent, ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconPlus, IconQrcode } from '@tabler/icons-react';
import { Alert, Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { api, getBaseUrl } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { getContrastColor, COLOR_SUCCESS, COLOR_WARNING, COLOR_INFO, COLOR_SECONDARY, COLOR_DANGER, COLOR_DARK } from '../../shared/colorUtils';
import { usePermissions } from '../../shared/permissions';
import BulkGenerateUnitsModal from './BulkGenerateUnitsModal';
import QRScannerModal from './QRScannerModal';
import InfiniteScrollBox from './components/InfiniteScrollBox';
import TableSearchBar from './components/TableSearchBar';
import { useDebouncedSearch } from './hooks/useDebouncedSearch';

interface StockItemInfo {
  id: number;
  product: number;
  product_name: string;
  stock_name: string;
  quantity_owned: number;
  tracking_type: string;
}

interface ItemUnit {
  id: number;
  stock_item: number;
  serial_number: string;
  qr_code: string;
  barcode: string | null;
  status: string;
  status_display: string;
  condition: string;
  condition_display: string;
  current_rental: number | null;
  current_rental_ref: string | null;
  current_project: number | null;
  current_project_ref: string | null;
  current_contract: number | null;
  current_contract_ref: string | null;
  location_note: string;
  notes: string;
}

interface UnitsPage {
  results?: ItemUnit[];
  items?: ItemUnit[];
  next?: string | null;
}

interface Movement {
  id: number;
  event_type: string;
  event_type_display: string;
  performed_by_name: string;
  location_note: string;
  notes: string;
  timestamp: string;
}

const STATUS_COLORS: Record<string, string> = {
  available: COLOR_SUCCESS,
  rented: COLOR_WARNING,
  in_project: COLOR_INFO,
  maintenance: COLOR_SECONDARY,
  lost: COLOR_DANGER,
  retired: COLOR_DARK,
};

const STATUS_OPTIONS = ['available', 'rented', 'in_project', 'maintenance', 'lost', 'retired'];
const CONDITION_OPTIONS = ['good', 'fair', 'damaged', 'written_off'];

const asList = (response: UnitsPage | ItemUnit[] | undefined): ItemUnit[] => {
  if (Array.isArray(response)) return response;
  return response?.results || response?.items || [];
};

const qrImageUrl = (unitId: number) => `${getBaseUrl()}/warehouses/api/item-units/${unitId}/qr_image/`;

interface InfiniteScrollProps {
  children: ReactNode;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}
const TypedInfiniteScrollBox = InfiniteScrollBox as unknown as ComponentType<InfiniteScrollProps>;

interface QRScannerModalProps {
  contextType?: string;
  contextId?: number | null;
  onClose: () => void;
  onCheckOut?: (unit: unknown) => void;
}
const TypedQRScannerModal = QRScannerModal as unknown as ComponentType<QRScannerModalProps>;

export default function ItemUnitList() {
  const { stockItemId } = useParams<{ stockItemId: string }>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasPerm } = usePermissions();
  const { searchInput, search, handleSearchInputChange, handleSearchSubmit } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [detailUnit, setDetailUnit] = useState<ItemUnit | null>(null);

  const { data: stockItem, isLoading: stockItemLoading } = useQuery<StockItemInfo>({
    queryKey: ['stock-item', stockItemId],
    queryFn: () => api.get<StockItemInfo>(`warehouses/api/stock-items/${stockItemId}/`),
    enabled: Boolean(stockItemId),
  });

  const unitsKey = ['item-units', stockItemId, search, statusFilter];
  const { data, isLoading: unitsLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<UnitsPage>({
    queryKey: unitsKey,
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ stock_item: stockItemId || '', page: String(pageParam), ordering: 'serial_number' });
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      return api.getRaw(`warehouses/api/item-units/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next ? new URL(lastPage.next).searchParams.get('page') : undefined,
    enabled: Boolean(stockItemId),
  });
  const units = useMemo(() => data?.pages.flatMap(asList) || [], [data]);
  const loading = stockItemLoading || unitsLoading;

  const invalidateUnits = () => queryClient.invalidateQueries({ queryKey: ['item-units', stockItemId] });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.itemunit') invalidateUnits();
    if (e.model === 'warehouses.itemunitmovement' && detailUnit) {
      queryClient.invalidateQueries({ queryKey: ['item-unit-movements', detailUnit.id] });
    }
  });

  const checkIn = useMutation({
    mutationFn: (unitId: number) => api.post(`warehouses/api/item-units/${unitId}/check_in/`, {}),
    onSuccess: (updated: ItemUnit) => {
      invalidateUnits();
      setDetailUnit((current) => (current && current.id === updated.id ? updated : current));
    },
  });

  const { data: movements = [], isLoading: movementsLoading } = useQuery<Movement[]>({
    queryKey: ['item-unit-movements', detailUnit?.id],
    queryFn: () => api.get<Movement[]>(`warehouses/api/item-units/${detailUnit?.id}/movements/`),
    enabled: Boolean(detailUnit),
  });

  const [detailForm, setDetailForm] = useState({ condition: 'good', location_note: '', notes: '' });
  const [detailError, setDetailError] = useState<string | null>(null);

  const openDetail = (unit: ItemUnit) => {
    setDetailUnit(unit);
    setDetailForm({ condition: unit.condition, location_note: unit.location_note || '', notes: unit.notes || '' });
    setDetailError(null);
  };
  const closeDetail = () => setDetailUnit(null);

  const saveDetail = useMutation({
    mutationFn: () => api.patch<ItemUnit>(`warehouses/api/item-units/${detailUnit?.id}/`, detailForm),
    onSuccess: (updated) => {
      invalidateUnits();
      setDetailUnit(updated);
    },
    onError: (requestError: any) => {
      const data = requestError?.response?.data;
      setDetailError(data?.detail || t('warehouses.units.saveError'));
    },
  });

  const handleDetailSubmit = (event: FormEvent) => {
    event.preventDefault();
    setDetailError(null);
    saveDetail.mutate();
  };

  const assignedToLink = (unit: ItemUnit) => {
    if (unit.current_project_ref) return <Link to={`/projects/detail/${unit.current_project}`}>{unit.current_project_ref}</Link>;
    if (unit.current_contract_ref) return <Link to={`/contracts/contract/${unit.current_contract}`}>{unit.current_contract_ref}</Link>;
    if (unit.current_rental_ref) return <Link to={`/rentals/rental/${unit.current_rental}`}>{unit.current_rental_ref}</Link>;
    return <span className="text-secondary">—</span>;
  };

  if (stockItemLoading && !stockItem) {
    return <PageShell pretitle={t('warehouses.units.title')} title={t('warehouses.units.title')}><PageSection><CenteredLoader /></PageSection></PageShell>;
  }

  return <PageShell
    pretitle={t('warehouses.units.title')}
    title={stockItem ? `${stockItem.product_name} · ${stockItem.stock_name}` : t('warehouses.units.title')}
    description={t('warehouses.units.pageDescription')}
    actions={<div className="d-flex flex-wrap gap-2">
      {stockItem && <Button as={Link as any} to={`/warehouses/products/${stockItem.product}`} variant="outline-secondary"><IconArrowLeft size={16} className="me-1" />{t('warehouses.products.detail')}</Button>}
      <Button variant="outline-primary" onClick={() => setShowScanner(true)}><IconQrcode size={16} className="me-1" />{t('warehouses.scanner.scanButton')}</Button>
      {hasPerm('warehouses.add_itemunit') && <Button variant="primary" onClick={() => setShowBulkModal(true)}><IconPlus size={16} className="me-1" />{t('warehouses.units.addUnits')}</Button>}
    </div>}
  >
    <PageSection>
      <div className="d-flex flex-column flex-md-row mb-3 gap-2">
        <TableSearchBar value={searchInput} onChange={handleSearchInputChange} onSubmit={handleSearchSubmit} placeholder={t('warehouses.units.searchPlaceholder')} submitLabel={t('warehouses.inventory.search')} />
        <Form.Select style={{ maxWidth: 220 }} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">{t('warehouses.units.filterAllStatuses')}</option>
          {STATUS_OPTIONS.map((code) => <option key={code} value={code}>{t(`warehouses.units.status_${code}`)}</option>)}
        </Form.Select>
      </div>

      {loading ? <CenteredLoader /> : units.length === 0 ? (
        <EmptyState
          className="py-5"
          icon={<i className="ti ti-package-off app-empty-state__icon-glyph" aria-hidden="true" />}
          title={t('warehouses.units.noUnits')}
          message={t('warehouses.units.noUnitsMessage')}
        />
      ) : (
        <TypedInfiniteScrollBox hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage}>
          <Table hover className="table-vcenter mb-0"><thead style={{ position: 'sticky', top: 0, background: 'var(--tblr-bg-surface, white)', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}><tr>
            <th>{t('warehouses.units.qrCode')}</th>
            <th>{t('warehouses.units.serialNumber')}</th>
            <th>{t('warehouses.units.status')}</th>
            <th>{t('warehouses.units.condition')}</th>
            <th>{t('warehouses.units.location')}</th>
            <th>{t('warehouses.units.assignedTo')}</th>
            <th>{t('common.actions')}</th>
          </tr></thead><tbody>{units.map((unit) => {
            const statusColor = STATUS_COLORS[unit.status] || COLOR_SECONDARY;
            return <tr key={unit.id}>
              <td>
                <button type="button" className="btn btn-sm btn-ghost-secondary p-1" onClick={() => openDetail(unit)} title={t('warehouses.units.qrCode')}>
                  <img src={qrImageUrl(unit.id)} alt={unit.qr_code} width={32} height={32} />
                </button>
              </td>
              <td>{unit.serial_number || <span className="text-secondary">—</span>}</td>
              <td><Badge style={{ backgroundColor: statusColor, color: getContrastColor(statusColor) }}>{unit.status_display}</Badge></td>
              <td>{unit.condition_display}</td>
              <td>{unit.location_note || <span className="text-secondary">—</span>}</td>
              <td>{assignedToLink(unit)}</td>
              <td>
                <div className="btn-group">
                  <Button size="sm" variant="outline-secondary" onClick={() => openDetail(unit)}>{t('warehouses.units.detail')}</Button>
                  {hasPerm('warehouses.change_itemunit') && (unit.status === 'rented' || unit.status === 'in_project') && (
                    <Button size="sm" variant="outline-warning" disabled={checkIn.isPending} onClick={() => checkIn.mutate(unit.id)}>
                      {t('warehouses.scanner.actionCheckIn')}
                    </Button>
                  )}
                </div>
              </td>
            </tr>;
          })}</tbody></Table>
        </TypedInfiniteScrollBox>
      )}
    </PageSection>

    <Modal show={Boolean(detailUnit)} onHide={closeDetail}>
      <Modal.Header closeButton>
        <Modal.Title>{t('warehouses.units.detail')}{detailUnit?.serial_number ? `: ${detailUnit.serial_number}` : ''}</Modal.Title>
      </Modal.Header>
      {detailUnit && (
        <Form onSubmit={handleDetailSubmit}>
          <Modal.Body>
            {detailError && <Alert variant="danger" className="mb-3">{detailError}</Alert>}
            <div className="d-flex align-items-center gap-3 mb-3">
              <img src={qrImageUrl(detailUnit.id)} alt={detailUnit.qr_code} width={96} height={96} />
              <div>
                <div className="text-secondary small">{t('warehouses.units.qrCode')}</div>
                <code>{detailUnit.qr_code}</code>
                <div className="mt-1">
                  <Badge style={{ backgroundColor: STATUS_COLORS[detailUnit.status] || COLOR_SECONDARY, color: getContrastColor(STATUS_COLORS[detailUnit.status] || COLOR_SECONDARY) }}>
                    {detailUnit.status_display}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group controlId="unit-condition">
                  <Form.Label>{t('warehouses.units.condition')}</Form.Label>
                  <Form.Select value={detailForm.condition} onChange={(e) => setDetailForm((prev) => ({ ...prev, condition: e.target.value }))}>
                    {CONDITION_OPTIONS.map((code) => <option key={code} value={code}>{t(`warehouses.units.condition_${code}`)}</option>)}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="unit-location">
                  <Form.Label>{t('warehouses.units.location')}</Form.Label>
                  <Form.Control value={detailForm.location_note} onChange={(e) => setDetailForm((prev) => ({ ...prev, location_note: e.target.value }))} />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3" controlId="unit-notes">
              <Form.Label>{t('warehouses.bundles.notes')}</Form.Label>
              <Form.Control as="textarea" rows={2} value={detailForm.notes} onChange={(e) => setDetailForm((prev) => ({ ...prev, notes: e.target.value }))} />
            </Form.Group>

            {(detailUnit.status === 'rented' || detailUnit.status === 'in_project') && hasPerm('warehouses.change_itemunit') && (
              <Button variant="outline-warning" size="sm" className="mb-3" disabled={checkIn.isPending} onClick={() => checkIn.mutate(detailUnit.id)}>
                {t('warehouses.scanner.actionCheckIn')}
              </Button>
            )}

            <h6 className="text-secondary mt-4 mb-2">{t('warehouses.units.movementHistory')}</h6>
            {movementsLoading ? <CenteredLoader /> : movements.length === 0 ? (
              <div className="text-secondary small">{t('warehouses.units.noMovements')}</div>
            ) : (
              <ul className="list-unstyled small mb-0" style={{ maxHeight: 200, overflowY: 'auto' }}>
                {movements.map((movement) => (
                  <li key={movement.id} className="border-bottom py-1">
                    <span className="fw-semibold">{movement.event_type_display}</span>{' '}
                    <span className="text-secondary">{new Date(movement.timestamp).toLocaleString()}</span>
                    {movement.performed_by_name && <span className="text-secondary"> · {movement.performed_by_name}</span>}
                    {movement.notes && <div className="text-secondary">{movement.notes}</div>}
                  </li>
                ))}
              </ul>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDetail} disabled={saveDetail.isPending}>{t('common.cancel')}</Button>
            <Button variant="primary" type="submit" disabled={saveDetail.isPending}>{saveDetail.isPending ? t('common.saving') : t('common.save')}</Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>

    {showScanner && <TypedQRScannerModal contextType="none" onClose={() => setShowScanner(false)} onCheckOut={() => invalidateUnits()} />}
    {showBulkModal && stockItem && (
      <BulkGenerateUnitsModal
        stockItemId={stockItem.id}
        itemName={`${stockItem.product_name} · ${stockItem.stock_name}`}
        onClose={() => setShowBulkModal(false)}
        onCreated={() => { invalidateUnits(); setShowBulkModal(false); }}
      />
    )}
  </PageShell>;
}
